import { UseFormProps } from 'react-hook-form';
import { z } from 'zod';
import { createZodFormResolver, Validation } from '@mews/framework';
import { EnterpriseScopeCounterDiscriminator } from '@mews-commander/data-access';
import { useFormValueSelector } from '@mews-commander/legacy-form';
import { useIsNonRegionalCreditNoteCounterEnabled } from '../../helpers';

export enum CounterUseFor {
    bills = 'bills',
    invoices = 'invoices',
}

export enum CounterFormField {
    Name = 'name',
    StartValue = 'startValue',
    NumberFormat = 'numberFormat',
    BillTypeCode = 'billTypeCode',
    IsDefault = 'isDefault',
    IsDefaultForCreditNotes = 'isDefaultForCreditNotes',
    Title = 'title',
    UseFor = 'useFor',
    Prefix = 'prefix',
    NumberOfDigits = 'numberOfDigits',
    Created = 'created',
    Updated = 'updated',
    DisplayCID = 'displayCID',
    CreditNoteTitle = 'creditNoteTitle',
    CounterId = 'counterId',
}

export interface CounterFormValues {
    [CounterFormField.Name]: string;
    [CounterFormField.StartValue]: number;
    [CounterFormField.IsDefault]: boolean;
    [CounterFormField.IsDefaultForCreditNotes]?: boolean;
    [CounterFormField.NumberFormat]?: string;
    [CounterFormField.BillTypeCode]?: string;
    [CounterFormField.Created]?: string;
    [CounterFormField.Updated]?: string;
    [CounterFormField.Title]?: string;
    [CounterFormField.DisplayCID]?: boolean;
    [CounterFormField.CreditNoteTitle]?: string;
}

export const defaultEmptyValues = {
    [CounterFormField.Name]: '',
    [CounterFormField.Title]: '',
    [CounterFormField.NumberFormat]: '',
    [CounterFormField.BillTypeCode]: '',
    [CounterFormField.StartValue]: 1,
    [CounterFormField.IsDefault]: false,
    [CounterFormField.IsDefaultForCreditNotes]: true,
};

const createValidationSchema = (isEditForm?: boolean) =>
    z.object({
        [CounterFormField.Name]: Validation.String.Required,
        [CounterFormField.IsDefault]: z.boolean(),
        [CounterFormField.CreditNoteTitle]: z.string().optional(),
        ...(isEditForm ? {} : { [CounterFormField.StartValue]: Validation.Number.MaxPositiveInteger }),
    });

const createValidationSchemaWithCreditNote = ({
    isEditForm,
    counterType,
}: {
    isEditForm?: boolean;
    counterType: EnterpriseScopeCounterDiscriminator;
}) => {
    const validationSchema = createValidationSchema(isEditForm);
    return validationSchema.superRefine((schemaValue, ctx) => {
        if (
            schemaValue[CounterFormField.IsDefault] &&
            !schemaValue[CounterFormField.CreditNoteTitle] &&
            counterType === EnterpriseScopeCounterDiscriminator.BillCounter
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.too_small,
                path: [CounterFormField.CreditNoteTitle],
                minimum: 1,
                type: 'string',
                inclusive: true,
            });
        }
    });
};

interface UseCounterFormProps {
    defaultValues?: CounterFormValues;
    isEditForm?: boolean;
    name: string;
}

export const useCountersForm = ({
    defaultValues,
    isEditForm,
    name,
}: UseCounterFormProps): UseFormProps<CounterFormValues> => {
    const counterType: EnterpriseScopeCounterDiscriminator = useFormValueSelector(name, 'counterType');
    const creditNoteBillCounterEnabled = useIsNonRegionalCreditNoteCounterEnabled();

    const validation = creditNoteBillCounterEnabled
        ? createValidationSchemaWithCreditNote({ isEditForm, counterType })
        : createValidationSchema(isEditForm);
    const resolver = createZodFormResolver(validation);

    return {
        resolver,
        mode: 'onChange',
        defaultValues: defaultValues || defaultEmptyValues,
    };
};
